import type { Session, User } from '@supabase/supabase-js'
import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { clearLegacySharedSession, supabase } from '../lib/supabase'
import { dashboardLoginUrl } from '../appUtils'

let authInitStarted = false
let authListenerRegistered = false
// True while a one-time handoff token is being redeemed. Supabase emits an
// INITIAL_SESSION event with no session before verifyOtp resolves; this flag lets
// the listener ignore that transient null instead of flipping the app to
// "signed out" and bouncing back to the dashboard mid-redemption.
let handoffPending = false
// True mientras se descarta una sesión que no era de la cuenta pedida. El
// SIGNED_OUT que emite supabase llegaría antes de que el navegador salga hacia
// el dashboard, y la vista de «sin sesión» dispararía su propio redirect
// consumiendo un intento de handoff de más.
let reauthPending = false

function takeEditorHandoffToken(): string | null {
  if (typeof window === 'undefined') return null
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''))
  const tokenHash = hash.get('veltica_handoff')
  if (!tokenHash) return null

  // Remove the one-time credential before making the network request so it
  // never remains in browser history, copied links or error screenshots.
  hash.delete('veltica_handoff')
  const nextHash = hash.toString()
  window.history.replaceState(
    null,
    '',
    `${window.location.pathname}${window.location.search}${nextHash ? `#${nextHash}` : ''}`,
  )
  return tokenHash
}

type AuthState = {
  user: User | null
  session: Session | null
  /** JWT access token — passed to the worker API */
  token: string | null
  initialized: boolean

  /**
   * Call once on app mount.
   * Loads the current session and subscribes to future auth changes.
   */
  init: () => void
  signOut: () => Promise<void>
  /**
   * Descarta la sesión de este origen y vuelve al dashboard a por un handoff
   * nuevo. Es la salida cuando el editor quedó autenticado con otra cuenta.
   */
  signOutAndReauth: () => Promise<void>
}

export const useAuthStore = create<AuthState>()(
  devtools(
    (set) => ({
      user: null,
      session: null,
      token: null,
      initialized: false,

      init: () => {
        if (authInitStarted) return
        authInitStarted = true

        // Read the one-time handoff token synchronously, before registering the
        // auth listener — otherwise the listener can observe the empty initial
        // session and mark auth "initialized" while verifyOtp is still in flight.
        const tokenHash = takeEditorHandoffToken()
        handoffPending = tokenHash !== null

        if (!authListenerRegistered) {
          authListenerRegistered = true
          supabase.auth.onAuthStateChange((_event, session) => {
            // Ignore the transient null session while a handoff is being redeemed
            // so the app doesn't bounce to the dashboard and abort verifyOtp.
            if (handoffPending && !session) return
            if (reauthPending) return
            set(
              {
                session,
                user: session?.user ?? null,
                token: session?.access_token ?? null,
                initialized: true,
              },
              false,
              'authStateChange'
            )
          })
        }

        void (async () => {
          if (tokenHash) {
            // generate_link(type=magiclink) is consumed as an email token and
            // creates a brand-new refresh-token chain local to editor.veltica.com.
            const { error } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: 'magiclink' })
            if (error) throw error
          }

          const { data } = await supabase.auth.getSession()
          // Wait until the editor has its own session before removing the old
          // cross-subdomain cookie. If the editor is visited first during the
          // rollout, the dashboard can still migrate that legacy session.
          if (data.session) clearLegacySharedSession()
          handoffPending = false
          // A completed handoff means the redirect loop is broken — reset the guard.
          try { sessionStorage.removeItem('veltica_handoff_attempts') } catch { /* private mode */ }
          set(
            {
              session: data.session,
              user: data.session?.user ?? null,
              token: data.session?.access_token ?? null,
              initialized: true,
            },
            false,
            'init/session'
          )
        })().catch(() => {
          handoffPending = false
          set({ user: null, session: null, token: null, initialized: true }, false, 'init/error')
        })
      },

      signOut: async () => {
        await supabase.auth.signOut()
        set({ user: null, session: null, token: null, initialized: true }, false, 'signOut')
      },

      signOutAndReauth: async () => {
        reauthPending = true
        // Scope local: sólo sobra la sesión de este origen. Con el scope global
        // que trae supabase por defecto también se cerraría la del dashboard,
        // que es justo la que tiene que emitir el handoff siguiente.
        try { await supabase.auth.signOut({ scope: 'local' }) } catch { /* la copia local se va igual */ }
        window.location.replace(dashboardLoginUrl())
      },
    }),
    { name: 'AuthStore', enabled: import.meta.env.DEV }
  )
)
